import { useEffect, useRef, useState } from 'react';
import { api } from '../../api/remembrApi';
import type { ResolvedItem } from '../../api/types';

/**
 * useCrossGroupSearch — Sprint 4 / Chunk 10B · FE-10.7.
 *
 * Debounced wrapper around `GET /search/resolve`. Returns the
 * current result list + loading + error state. Empty / whitespace
 * queries clear the result list without firing a request.
 *
 * Why a hook instead of a Redux slice? Search results are
 * inherently ephemeral and the screen only consumes them in one
 * place. Adding a slice would force every other consumer to read
 * the cache and worry about staleness. Local state is simpler.
 *
 * The hook tracks an in-flight request id so a stale response
 * (a slow request that resolved AFTER the user typed more) can't
 * overwrite the latest results.
 */

const DEBOUNCE_MS = 250;
const PAGE_SIZE = 20;

export interface UseCrossGroupSearchResult {
  /** Current query (controlled — caller drives setQuery). */
  query: string;
  setQuery: (q: string) => void;
  /** Current loading state. */
  loading: boolean;
  /** First-page items. */
  items: ResolvedItem[];
  /** Total matches across all pages. */
  total: number;
  /** True when a Load-more page exists server-side. */
  hasMore: boolean;
  /** Trigger an append of the next page. */
  loadMore: () => void;
  /** Last error message or null. */
  error: string | null;
}

export function useCrossGroupSearch(): UseCrossGroupSearchResult {
  const [query, setQuery] = useState('');
  const [items, setItems] = useState<ResolvedItem[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const offsetRef = useRef(0);
  const requestIdRef = useRef(0);
  const debouncedQueryRef = useRef('');

  // Debounce + fire the initial-page request whenever the query
  // changes. Empty / whitespace clears state without a request.
  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      setItems([]);
      setTotal(0);
      setLoading(false);
      setError(null);
      offsetRef.current = 0;
      debouncedQueryRef.current = '';
      return;
    }

    const handle = setTimeout(() => {
      const myRequestId = ++requestIdRef.current;
      offsetRef.current = 0;
      debouncedQueryRef.current = trimmed;
      setLoading(true);
      setError(null);
      api
        .resolveSearch(trimmed, { limit: PAGE_SIZE, offset: 0 })
        .then((res) => {
          if (requestIdRef.current !== myRequestId) return;
          setItems(res.items);
          setTotal(res.total);
          offsetRef.current = res.items.length;
          setLoading(false);
        })
        .catch((err: any) => {
          if (requestIdRef.current !== myRequestId) return;
          setError(
            err?.response?.data?.detail || err?.message || 'Search failed',
          );
          setLoading(false);
        });
    }, DEBOUNCE_MS);

    return () => clearTimeout(handle);
  }, [query]);

  const loadMore = () => {
    if (loading || items.length >= total) return;
    const trimmed = debouncedQueryRef.current;
    if (!trimmed) return;
    const myRequestId = ++requestIdRef.current;
    setLoading(true);
    api
      .resolveSearch(trimmed, {
        limit: PAGE_SIZE,
        offset: offsetRef.current,
      })
      .then((res) => {
        if (requestIdRef.current !== myRequestId) return;
        setItems((prev) => {
          // Dedupe on item_id in case the backend echoes a row
          // (concurrent write etc).
          const seen = new Set(prev.map((it) => it.item_id));
          return [...prev, ...res.items.filter((it) => !seen.has(it.item_id))];
        });
        setTotal(res.total);
        offsetRef.current += res.items.length;
        setLoading(false);
      })
      .catch((err: any) => {
        if (requestIdRef.current !== myRequestId) return;
        setError(
          err?.response?.data?.detail || err?.message || 'Search failed',
        );
        setLoading(false);
      });
  };

  return {
    query,
    setQuery,
    loading,
    items,
    total,
    hasMore: items.length < total,
    loadMore,
    error,
  };
}
